<template>
  <div class="tool_container">
    <div v-if="!data?.attributes?.content || isMobile()">
      <div class="banner" :style="{ backgroundImage: backImg }">
        <div class="inner_box">
          <Breadcrumb v-if="params.countryName !== 'global'" :routes="routes" reversed-color class="bread" />
          <div class="title">{{ category.title }}</div>
          <div class="desc">{{ headerInfo.desc }}</div>
        </div>
      </div>
      <div class="content_box">
        <div class="search_box">
          <div class="title">选择查看国家</div>
          <div class="select_box">
            <span class="country_text">国家</span>
            <div class="select">
              <a-select v-model="activeCountry" placeholder="请选择国家" :bordered="false">
                <a-option v-for="country in countryList" :key="country.id" :value="country.attributes.name">
                  {{ country.attributes.cnName }}
                </a-option>
              </a-select>
            </div>
          </div>
          <a-button type="primary" class="button" @click="onSubmitClick">查看</a-button>
        </div>
      </div>
    </div>
    <div class="content_result-box" v-if="data?.attributes?.content">
      <div class="container">
        <Breadcrumb v-if="params.countryName !== 'global'" :routes="routes" class="bread" />
        <div class="title">{{ headerInfo.title }}</div>
        <div class="select_box">
          <span class="country_text">国家</span>
          <div class="select">
            <a-select v-model="activeCountry" placeholder="请选择国家" :bordered="false" @change="onSubmitClick">
              <a-option v-for="country in countryList" :key="country.id" :value="country.attributes.name">
                {{ country.attributes.cnName }}
              </a-option>
            </a-select>
            <!-- <div>
              <a-button type="primary" class="search" @click="onSubmitClick">
                查看
                <img style="width: 25px" src="@/assets/icons/arrow.svg" alt="arrow" />
              </a-button>
            </div> -->
          </div>
        </div>
        <div class="content-wrapper">
          <div ref="markdownContainer" class="content markdown-content"></div>
          <EmploymentTools class="employment-tools" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { marked } from 'marked';
import { IEntity } from '~~/@types/cms';
import { isMobile } from '~~/common/utils';
import qs from 'qs';
import { ICountry, IGlobalAddition } from '~~/@types/entities';

type TMapKey =
  | 'corporate_tax'
  | 'labor_employment_regulations'
  | 'government_departments_relevant_institutions'
  | 'banks_insurance_companies';

interface IMap {
  title: string;
  desc: string;
  img: string;
  mobileImg: string;
  name: TMapKey;
  headTitle: string;
}

const bannerMap = new Map<TMapKey, IMap>([
  [
    'corporate_tax',
    {
      title: '全球税收政策',
      desc: '帮您了解全球各国税收政策',
      img: 'https://video.reta-inc.com/image/default/F992C3265FD54C1FA82A34AA3776C1B2-6-2.png',
      mobileImg: 'https://video.reta-inc.com/image/default/933980E0D2DA49688E58A01D80CFBF76-6-2.png',
      name: 'corporate_tax',
      headTitle: '用工成本 | 一站式全球用工平台  | dghire',
    },
  ],
  [
    'labor_employment_regulations',
    {
      title: '全球劳动法',
      desc: '帮您了解全球各国劳动就业法规政策',
      img: 'https://video.reta-inc.com/image/default/AA3A83B68D2C40158D1C407B4037FEF2-6-2.png',
      mobileImg: 'https://video.reta-inc.com/image/default/D5EF402ADA4441F2B39C2F6B60B8280E-6-2.png',
      name: 'labor_employment_regulations',
      headTitle: '劳动法 | 一站式全球用工平台  | dghire',
    },
  ],
  [
    'government_departments_relevant_institutions',
    {
      title: '全球政府机构',
      desc: '查看各国政府部门和相关机构联系方式',
      img: 'https://video.reta-inc.com/image/default/C66741AE0AAF44E1B4B4F723399B8DA9-6-2.png',
      mobileImg: 'https://video.reta-inc.com/image/default/8D33C8B1ABCD4325A58941727A62CC9F-6-2.png',
      name: 'government_departments_relevant_institutions',
      headTitle: '工作签证 | 一站式全球用工平台  | dghire',
    },
  ],
  [
    'banks_insurance_companies',
    {
      title: '全球银行保险机构',
      desc: '查看各国当地银行、保险公司的名单和联系方式',
      img: 'https://video.reta-inc.com/image/default/BFE692C337BD44FABD1CB4ADE3871724-6-2.png',
      mobileImg: 'https://video.reta-inc.com/image/default/697A42C2F6CF40D880802B3160B38A59-6-2.png',
      name: 'banks_insurance_companies',
      headTitle: '员工保险 | 一站式全球用工平台  | dghire',
    },
  ],
]);

const params = useRoute().params;
if (!params.category || !params.countryName) {
  throw createError({ statusCode: 404 });
}

const { data, refresh } = await useAsyncData(async () => {
  const { category, countryName } = useRoute().params;
  const query = {
    category: (category as string).replace(/-/g, '_'),
    countryName,
  };
  const { data } = await useFetch<IEntity<IGlobalAddition>>(`/api/resource/additional/detail?${qs.stringify(query)}`);
  return data.value;
});
if (!data.value?.id) {
  throw createError({ statusCode: 404 });
}

const {
  category: {
    data: { attributes: category },
  },
  country: {
    data: { attributes: country },
  },
} = data.value?.attributes as IGlobalAddition;

const routes = [
  { text: '资源中心' },
  { text: '全球雇佣指南', path: '/resource/global' },
  { text: `${country.cnName}`, path: `/resource/global/${country.name}` },
  { text: `${category.title}` },
];

const activeCountry = ref(country.name);
const countryList = ref<IEntity<ICountry>[]>([]);
onMounted(async () => {
  const response = await fetch(`/api/resource/additional/countries?category=${category.name}`);
  const data = (await response.json()) as IEntity<ICountry>[];
  countryList.value = data;
});

const headerInfo = bannerMap.get(category.name as TMapKey)!;
const backImg = ref('');
onMounted(() => {
  const pcBackGroundImg = `url(${headerInfo.img})`;
  const mbBackGroundImg = `url(${headerInfo.mobileImg})`;
  backImg.value = isMobile() ? mbBackGroundImg : pcBackGroundImg;
});

useSeoHead({
  title: country.cnName,
});


const globalLoading = inject('global-loading', ref(false));
async function onSubmitClick() {
  if (!activeCountry.value) {
    return;
  }
  const route = useRoute();
  await navigateTo({
    ...route,
    params: {
      countryName: activeCountry.value,
    },
  });
  globalLoading.value = true;
  await refresh();
  globalLoading.value = false;
}

const markdownContainer = ref<HTMLDivElement>();
watchEffect(() => {
  if (!data.value?.attributes?.content) {
    return;
  }
  const cnName = countryList.value.find(item => item.attributes.name === country.name)?.attributes.cnName ?? '';
  if (!cnName) {
    return;
  }
  const html = marked.parse(data.value?.attributes?.content);
  const div = document.createElement('div');
  div.innerHTML = html;
  const firstEle = div.firstElementChild!;
  if (firstEle.tagName !== 'H3') {
    const h3 = document.createElement('h3');
    h3.innerHTML = `${cnName}${headerInfo.title}`;
    div.prepend(h3);
  } else {
    firstEle.textContent = cnName + firstEle.textContent;
  }

  markdownContainer.value?.appendChild(document.adoptNode(div));
});
</script>

<style lang="less" scoped>
@media screen and(min-width: 800px) {
  .tool_container {
    .banner {
      height: 360px;
      background-size: 100% 100%;
      background-repeat: no-repeat;
      color: #ffffff;
      text-align: center;

      .inner_box {
        max-width: 1280px;
        margin: 0 auto;
        padding-top: 25px;

        .title {
          margin-top: 53px;
          font-weight: 600;
          font-size: 48px;
          line-height: 67px;
        }

        .desc {
          margin-top: 20px;
          font-size: 24px;
          line-height: 34px;
        }
      }
    }

    .content_box {
      background: #f5f8ff;
      padding: 1px 1px 24px 1px;

      .search_box {
        margin: -100px auto 0;
        width: 680px;
        height: 250px;
        background: #ffffff;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        // justify-content: center;
        align-items: center;

        .title {
          font-weight: 500;
          font-size: 24px;
          line-height: 34px;
          color: #1d2129;
          margin-top: 32px;
        }

        .select_box {
          margin-top: 24px;
          width: 380px;
          height: 48px;
          background: #ffffff;
          border: 1px solid #c9cdd4;
          border-radius: 4px;
          padding: 13px 0px 13px 20px;
          box-sizing: border-box;
          display: flex;
          align-items: center;

          .country_text {
            font-size: 18px;
            color: #1d2129;
            line-height: 22px;
            padding-right: 20px;
            position: relative;

            &::after {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 1px;
              height: 100%;
              background: #e5e6eb;
              // vertical-align:middle;
            }
          }

          .select {
            flex: 1;
          }
        }

        .button {
          width: 400px;
          height: 48px;
          margin-top: 24px;
        }
      }
    }

    .content_result-box {
      background: #f5f8ff;
      padding: 25px 40px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .container {
        width: 1280px;

        .title {
          font-weight: 600;
          font-size: 20px;
          line-height: 28px;
          letter-spacing: 0.02em;
          color: #1d2129;
          margin-top: 25px;
        }

        .content-wrapper {
          display: flex;

          .content {
            max-width: 1280px;
            box-sizing: border-box;
            background: #ffffff;
            border-radius: 12px;
            margin-top: 24px;
            padding: 15px 40px;
          }

          .employment-tools {
            white-space: nowrap;
            margin-top: 20px;
            margin-left: 24px;
          }
        }

        .select_box {
          margin-top: 24px;
          width: 380px;
          height: 48px;
          background: #ffffff;
          border: 1px solid #e5e6eb;
          border-radius: 4px;
          padding: 13px 0px 13px 20px;
          box-sizing: border-box;
          display: flex;
          align-items: center;

          .country_text {
            padding-right: 20px;
            font-size: 16px;
            line-height: 22px;
            color: #1d2129;
            position: relative;

            &::after {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 1px;
              height: 100%;
              background: #e5e6eb;
            }
          }

          .select {
            display: flex;
            flex: 1;

            .search {
              flex: 1;
              height: 40px;
              margin-right: 4px;
            }
          }
        }
      }
    }
  }
}
</style>
<style lang="less" scoped>
@media screen and(max-width: 800px) {
  .tool_container {
    .banner {
      height: 200px;
      background-size: 100% 100%;
      background-repeat: no-repeat;
      color: #ffffff;
      text-align: center;

      .inner_box {
        padding: 13px 16px 0;

        .title {
          margin-top: 21px;
          font-weight: 600;
          font-size: 28px;
          line-height: 39px;
        }

        .desc {
          margin-top: 20px;
          font-size: 24px;
          line-height: 34px;
        }
      }
    }

    .content_box {
      background: #f5f8ff;
      padding: 1px 1px 24px 1px;

      .search_box {
        margin: -20px 16px 0;
        height: 250px;
        background: #ffffff;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        // justify-content: center;
        align-items: center;

        .title {
          font-weight: 500;
          font-size: 24px;
          line-height: 34px;
          color: #1d2129;
          margin-top: 32px;
        }

        .select_box {
          margin: 24px auto 0;
          width: 90.6%;
          height: 48px;
          background: #ffffff;
          border: 1px solid #c9cdd4;
          border-radius: 4px;
          padding: 13px 0px 13px 20px;
          box-sizing: border-box;
          display: flex;
          align-items: center;

          .country_text {
            font-size: 18px;
            color: #1d2129;
            line-height: 22px;
            padding-right: 20px;
            position: relative;

            &::after {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 1px;
              height: 100%;
              background: #e5e6eb;
              // vertical-align:middle;
            }
          }

          .select {
            flex: 1;
          }
        }

        .button {
          width: 90.6%;
          height: 48px;
          margin-top: 24px;
        }
      }

      .content {
        margin: 16px 16px 24px;
        background: #ffffff;
        border-radius: 12px;
        padding: 16px;
        box-sizing: border-box;
        width: calc(100vw - 32px);
      }
    }

    .content_result-box {
      .container {
        display: flex;
        justify-content: center;
        background-color: #f5f8ff;

        .bread,
        .title,
        .select_box {
          display: none;
        }

        .content-wrapper {
          width: calc(100vw - 32px);
          box-sizing: border-box;
          background-color: #fff;
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 20px;

          .employment-tools {
            display: none;
          }
        }
      }
    }
  }
}
</style>
